import { useState, useCallback } from 'react';
import { Loader2, Ban, Eye, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function FieldRow({ label, before, after }) {
  const bStr = before != null ? (typeof before === 'number' ? before.toFixed(2) : String(before)) : '(null)';
  const aStr = after != null ? (typeof after === 'number' ? after.toFixed(2) : String(after)) : '(null)';
  const changed = bStr !== aStr;
  return (
    <tr className={changed ? 'text-amber-700 dark:text-amber-300' : ''}>
      <td className="py-1 pr-4 text-xs text-muted-foreground font-medium">{label}</td>
      <td className="py-1 pr-4 text-xs tabular-nums">{bStr}</td>
      <td className="py-1 text-xs tabular-nums font-medium">{aStr}</td>
    </tr>
  );
}

export function ManualCancelPanel() {
  const [uplistingId, setUplistingId] = useState('');
  const [cancellationFee, setCancellationFee] = useState('0');
  const [reason, setReason] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  // Track which inputs the preview was generated for, so Apply is
  // disabled if the user changes inputs after previewing.
  const [previewedInputs, setPreviewedInputs] = useState(null);

  const inputsMatch = previewedInputs
    && previewedInputs.id === uplistingId.trim()
    && previewedInputs.fee === cancellationFee;

  const canPreview = uplistingId.trim().length > 0;
  const canApply = preview && inputsMatch && reason.trim().length > 0 && !applying;

  const runPreview = useCallback(async () => {
    const id = uplistingId.trim();
    if (!id) return;
    setPreviewing(true);
    setPreview(null);
    setResult(null);
    try {
      const fee = parseFloat(cancellationFee) || 0;
      const { data } = await diagnosticsAPI.manualCancelPreview(id, fee);
      setPreview(data.preview);
      setPreviewedInputs({ id, fee: cancellationFee });
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewing(false);
    }
  }, [uplistingId, cancellationFee]);

  const runApply = useCallback(async () => {
    const id = uplistingId.trim();
    if (!id || !reason.trim()) return;
    setApplying(true);
    try {
      const fee = parseFloat(cancellationFee) || 0;
      const { data } = await diagnosticsAPI.manualCancel(id, {
        cancellationFee: fee,
        reason: reason.trim(),
        confirm: true,
      });
      setResult(data);
      setPreview(null);
      setPreviewedInputs(null);
      toast.success(`Booking ${id} manually cancelled`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setApplying(false);
    }
  }, [uplistingId, cancellationFee, reason]);

  const fillYerim = useCallback(() => {
    setUplistingId('10281472');
    setPreview(null);
    setPreviewedInputs(null);
    setResult(null);
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Ban className="h-5 w-5 text-red-500" />
          Manually Cancel Booking
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          For cases where Uplisting did not fire a cancellation event but the booking
          is genuinely cancelled. Sets status, zeroes revenue, computes owner cancellation
          revenue from any fee. Audited.
        </p>

        {/* Inputs */}
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-1">
              Uplisting ID
            </label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={uplistingId}
                onChange={(e) => {
                  setUplistingId(e.target.value);
                  setPreview(null);
                  setPreviewedInputs(null);
                  setResult(null);
                }}
                placeholder="e.g. 10281472"
                className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm w-40 font-mono"
              />
              <Button variant="outline" size="sm" onClick={fillYerim}>
                Yerim Lee (10281472)
              </Button>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-muted-foreground block mb-1">
              Cancellation fee EUR
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={cancellationFee}
              onChange={(e) => {
                setCancellationFee(e.target.value);
                setPreview(null);
                setPreviewedInputs(null);
              }}
              className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm w-32 tabular-nums"
            />
            <p className="text-[10px] text-muted-foreground mt-0.5">
              0 if free cancellation (default). Enter Uplisting-reported fee if known.
            </p>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-muted-foreground block mb-1">
            Reason (required)
          </label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Confirmed cancelled by guest 2026-05-15, no Uplisting webhook fired."
            className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm w-full"
          />
        </div>

        {/* Action buttons */}
        <div className="flex gap-2">
          <Button
            onClick={runPreview}
            disabled={!canPreview || previewing}
            variant="default"
          >
            {previewing ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Eye className="h-4 w-4 mr-2" />
            )}
            Preview
          </Button>

          <Button
            variant="destructive"
            onClick={runApply}
            disabled={!canApply}
          >
            {applying ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <AlertTriangle className="h-4 w-4 mr-2" />
            )}
            Apply
          </Button>
        </div>

        {/* Preview result */}
        {preview && (
          <div className="rounded-lg border p-3 bg-muted/30 space-y-3">
            <div className="flex items-center gap-2">
              <Eye className="h-4 w-4 text-blue-500" />
              <span className="text-sm font-medium">Preview (no write)</span>
            </div>
            <div className="text-xs space-y-1">
              <p><span className="text-muted-foreground">Guest:</span> {preview.guest_name}</p>
              <p><span className="text-muted-foreground">Property:</span> {preview.property_name}</p>
              <p><span className="text-muted-foreground">Check-in:</span> {preview.check_in} <span className="text-muted-foreground">Check-out:</span> {preview.check_out}</p>
              <p><span className="text-muted-foreground">Nights:</span> {preview.nights} <span className="text-muted-foreground">Channel:</span> {preview.channel} <span className="text-muted-foreground">Type:</span> {preview.management_type}</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-1 pr-4 text-xs font-medium">Field</th>
                    <th className="py-1 pr-4 text-xs font-medium">Before</th>
                    <th className="py-1 text-xs font-medium">After</th>
                  </tr>
                </thead>
                <tbody>
                  <FieldRow label="status" before={preview.before.status} after={preview.after.status} />
                  <FieldRow label="net_revenue" before={preview.before.net_revenue} after={preview.after.net_revenue} />
                  <FieldRow label="total_price" before={preview.before.total_price} after={preview.after.total_price} />
                  <FieldRow label="channel_commission" before={preview.before.channel_commission} after={preview.after.channel_commission} />
                  <FieldRow label="total_payout" before={preview.before.total_payout} after={preview.after.total_payout} />
                  <FieldRow label="br_commission_amount" before={preview.before.br_commission_amount} after={preview.after.br_commission_amount} />
                  <FieldRow label="management_fee" before={preview.before.management_fee} after={preview.after.management_fee} />
                  <FieldRow label="management_fee_vat" before={preview.before.management_fee_vat} after={preview.after.management_fee_vat} />
                  <FieldRow label="cancellation_fee" before={preview.before.cancellation_fee} after={preview.after.cancellation_fee} />
                  <FieldRow label="original_total_price" before={preview.before.original_total_price} after={preview.after.original_total_price} />
                  <FieldRow label="original_channel_commission" before={preview.before.original_channel_commission} after={preview.after.original_channel_commission} />
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Apply result */}
        {result && (
          <div className="rounded-lg border p-3 bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800 space-y-3">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
              <span className="text-sm font-medium text-green-800 dark:text-green-300">
                Cancellation applied
              </span>
              <Badge variant="outline" className="text-[10px]">{result.uplisting_id}</Badge>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-1 pr-4 text-xs font-medium">Field</th>
                    <th className="py-1 pr-4 text-xs font-medium">Before</th>
                    <th className="py-1 text-xs font-medium">After</th>
                  </tr>
                </thead>
                <tbody>
                  <FieldRow label="status" before={result.before.status} after={result.after.status} />
                  <FieldRow label="net_revenue" before={result.before.net_revenue} after={result.after.net_revenue} />
                  <FieldRow label="total_price" before={result.before.total_price} after={result.after.total_price} />
                  <FieldRow label="channel_commission" before={result.before.channel_commission} after={result.after.channel_commission} />
                  <FieldRow label="cancellation_fee" before={result.before.cancellation_fee} after={result.after.cancellation_fee} />
                  <FieldRow label="original_total_price" before={result.before.original_total_price} after={result.after.original_total_price} />
                  <FieldRow label="original_channel_commission" before={result.before.original_channel_commission} after={result.after.original_channel_commission} />
                </tbody>
              </table>
            </div>
            <div className="text-xs space-y-0.5 text-muted-foreground">
              <p><span className="font-medium">Actor:</span> {result.audit.manual_cancellation_actor}</p>
              <p><span className="font-medium">Reason:</span> {result.audit.manual_cancellation_reason}</p>
              <p><span className="font-medium">At:</span> {result.audit.manual_cancellation_at}</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
